import { Alert }                              from '@/components/ui/alert';
import CodeModel                              from '@/models/services/code/CodeModel';
import CodePropertyValueModel                 from '@/models/services/code/CodePropertyValueModel';
import DeliverableTransferStatusHistoryModel  from '@/models/services/shop/DeliverableTransferStatusHistoryModel';
import NotFoundPage                           from '@/pages/NotFound/NotFoundPage';
import PageContainer                          from '@/components/pageContainer/PageContainer';
import React                                  from 'react';
import Spinner                                from '@/components/spinner/Spinner';
import TicketPageContent                      from './components/TicketPageContent';
import TicketPageHeader                       from '@/pages/Ticket/components/TicketPageHeader';
import useApiCollection                       from '@widesk-core/hooks/useApiCollection';
import useFetch                               from '@widesk-core/hooks/useFetch';
import { useParams }                          from 'react-router-dom';
import { useTranslation }                     from 'react-i18next';
import Modal                                  from '@/components/modal/Modal';
import { ModalData }                          from '@/components/modal/Modal';
import { Code_CodePropertyReference }         from '@/models/services/code/CodePropertyModel';
import FondationsEntertainmentModel           from '@/models/services/fondations/EntertainmentModel';
import { getIdFromUrn }                       from '@widesk-core/tools/identifier';
import CodeTypePropertyValueModel             from '@/models/services/code/CodeTypePropertyValueModel';
import { when }                               from 'mobx';

const TicketPage = () => {
	const [ticketHasBeenTransfered, setTicketHasBeenTransfered] = React.useState(false);
	const [transferEmailDisplayed, setTransferEmailDisplayed] = React.useState('');

	const codeCollection = useApiCollection(CodeModel);
	const transferStatusHistoryCollection = useApiCollection(DeliverableTransferStatusHistoryModel);
	const codePropertyValueCollection = useApiCollection(CodePropertyValueModel);
	const codeTypePropertyValueCollection = useApiCollection(CodeTypePropertyValueModel);

	const [modalData, setModalData] = React.useState<ModalData | undefined>();
	const [modalIsOpen, setModalIsOpen] = React.useState(false);
	const [gespassEntertainment, setGespassEntertainment] = React.useState<FondationsEntertainmentModel | undefined>();

	const { codeValue } = useParams();
	const { t } = useTranslation();

	const transferIsWaiting =
		transferStatusHistoryCollection.first()?.deliverableTransferStatus.reference === 'waiting';

	const displayAlertMessage =
		!!transferEmailDisplayed && (ticketHasBeenTransfered || transferIsWaiting);

	const AlertMessage = () => {
		let title = '';
		let status: 'info' | 'warning' = 'info';

		if (ticketHasBeenTransfered) {
			title = t('pages.ticket.actions.transfer.success', { email: transferEmailDisplayed });
			status = 'info';
		}

		if (transferIsWaiting) {
			title = t('pages.ticket.actions.transfer.waiting', { email: transferEmailDisplayed });
			status = 'warning';
		}

		return <Alert status={status} title={title} />;
	};

	const { data: code, isLoading } = useFetch(async () => {
		await codeCollection.setFilter('value', [(codeValue as string)]).list();
		const code = codeCollection.first();

		if (!code) return;

		await when(() => code.codeKind.isLoaded);

		// Une contremarque doit remonter à son deliverable parent.
		if (code.isDeliverableExchange) {
			await CodeModel.loadDeliverables([code]);
		}

		const isGespass = code.isGespass;

		const references: Code_CodePropertyReference[] = isGespass
			? ['firstname', 'lastname', 'email']
			: ['owner_firstname', 'owner_lastname', 'owner_birthdate', 'owner_email'];

		await Promise.all([
			codePropertyValueCollection
				.setFilter('codeProperty.reference', references)
				.listBy(code.id, 'code')
				.then(coll => coll.whenIsLoaded(m => m.codeProperty)),

			codeTypePropertyValueCollection
				.setFilter('codeTypeProperty.reference', ['schedule'])
				.listBy(code.getId('codeType'), 'codeType'),

			transferStatusHistoryCollection
				.setFilter('deliverableTransfer.deliverable', code.deliverableId ? [code.deliverableId] : [])
				.setSort('createdAt', 'desc')
				.list()
				.then(async coll => {
					await Promise.all([
						coll.whenIsLoaded(m => m.deliverableTransfer),
						coll.whenIsLoaded(m => m.deliverableTransferStatus),
					]);
				}),

			codeCollection
				.whenIsLoaded(m => m.codeType)
				.then(async () => {
					await codeCollection.whenIsLoaded(m => isGespass ? m.codeType.codeEntertainment : m.codeType.source.product.entertainment);
				}),
		]);

		if (isGespass) {
			const entertainment = await new FondationsEntertainmentModel({
				id: getIdFromUrn(code.codeType.codeEntertainment.entertainmentUrn)
			}).fetch();

			setGespassEntertainment(entertainment);
		}

		const deliverableTransfer = transferStatusHistoryCollection.first()?.deliverableTransfer;
		
		if (deliverableTransfer) {
			setTransferEmailDisplayed(deliverableTransfer.email);
		}

		return code;
	}, true);

	if (isLoading) {
		return <Spinner />;
	}

	if (!code) {
		return <NotFoundPage />;
	}

	return (
		<PageContainer>
			<Modal
				model={modalData?.model}
				isDangerous={modalData?.isDangerous}
				isOpen={modalIsOpen}
				setIsOpen={setModalIsOpen}
				titleComponent={modalData?.titleComponent}
				bodyComponent={modalData?.bodyComponent}
				onConfirm={modalData?.onConfirm}
				onClose={modalData?.onClose}
				onSuccess={modalData?.onSuccess}
				onError={modalData?.onError}
			/>

			<TicketPageHeader
				code={code}
				codePropertyValueCollection={codePropertyValueCollection}
				ticketHasBeenTransfered={ticketHasBeenTransfered}
				setTicketHasBeenTransfered={setTicketHasBeenTransfered}
				setTransferEmailDisplayed={setTransferEmailDisplayed}
				transferStatusHistoryCollection={transferStatusHistoryCollection}
				setModalData={setModalData}
				setModalIsOpen={setModalIsOpen}
			/>

			<TicketPageContent
				code={code}
				codePropertyValueCollection={codePropertyValueCollection}
				codeTypePropertyValueCollection={codeTypePropertyValueCollection}
				gespassEntertainment={gespassEntertainment}
			/>

			{displayAlertMessage && <AlertMessage />}
		</PageContainer>
	);
};

export default TicketPage;
